import { useState, useCallback, useMemo, useRef } from 'react'
import type { OrderLine } from '../types'

/** Représentation d'une ligne dans le brouillon de validation */
export interface DraftLine {
   sku: string
   originalQty: number      // qty de original_lines (0 si ligne ajoutée par le valideur)
   draftQty: number          // qty modifiée par le valideur
   accepted: boolean         // true = acceptée, false = refusée
   deleted: boolean          // true = supprimée visuellement (reste affichée barrée jusqu'à validation)
   isNew: boolean            // true = ajoutée par le valideur
   price?: number
   comment?: string
   discount?: number
}

function buildDraftFromLines(lines: OrderLine[]): DraftLine[] {
   return lines.map(line => ({
      sku: line.sku,
      originalQty: line.qty,
      draftQty: line.qty,
      accepted: true,
      deleted: false,
      isNew: false,
      price: line.price,
      comment: line.comment,
      discount: line.discount,
   }))
}

/**
 * Construit le draft en mergeant l'état initial (original_lines) et l'état courant
 * (lines, potentiellement déjà modifié par une session précédente de validation).
 * - originalQty = qty initiale du client (source originalLines)
 * - draftQty = qty courante (source currentLines, fallback originalLines)
 * - isNew = SKU présent dans currentLines mais absent de originalLines
 * - deleted = SKU présent dans originalLines mais absent de currentLines
 */
function mergeDraftFromLines(originalLines: OrderLine[], currentLines: OrderLine[]): DraftLine[] {
   const origBySku = new Map(originalLines.map(l => [l.sku, l]))
   const currBySku = new Map(currentLines.map(l => [l.sku, l]))
   const allSkus = [...originalLines.map(l => l.sku), ...currentLines.filter(l => !origBySku.has(l.sku)).map(l => l.sku)]

   return allSkus.map(sku => {
      const orig = origBySku.get(sku)
      const curr = currBySku.get(sku)
      if (!orig && curr) {
         // Ligne ajoutée par le validateur lors d'une session précédente
         return { sku, originalQty: 0, draftQty: curr.qty, accepted: true, deleted: false, isNew: true, price: curr.price, comment: curr.comment, discount: curr.discount }
      }
      if (orig && !curr) {
         // Ligne supprimée par le validateur lors d'une session précédente
         return { sku, originalQty: orig.qty, draftQty: 0, accepted: false, deleted: true, isNew: false, price: orig.price, comment: orig.comment, discount: orig.discount }
      }
      // Ligne présente dans les deux : draftQty = valeur courante (potentiellement modifiée)
      return { sku, originalQty: orig!.qty, draftQty: curr!.qty, accepted: true, deleted: false, isNew: false, price: curr!.price, comment: curr!.comment, discount: curr!.discount }
   })
}

function useValidationDraft(originalLines: OrderLine[], currentLines?: OrderLine[]) {
   const initRef = useRef(originalLines)
   const [draftLines, setDraftLines] = useState<DraftLine[]>(() =>
      currentLines ? mergeDraftFromLines(originalLines, currentLines) : buildDraftFromLines(originalLines)
   )

   const isDirty = useMemo(() => {
      if (draftLines.length !== initRef.current.length) return true
      return draftLines.some((dl, i) => {
         const orig = initRef.current[i]
         if (!orig) return true
         return dl.sku !== orig.sku || dl.draftQty !== orig.qty || dl.deleted || !dl.accepted || dl.isNew
      })
   }, [draftLines])

   const setLineAccepted = useCallback((sku: string, accepted: boolean) => {
      setDraftLines(prev => prev.map(dl =>
         dl.sku === sku ? { ...dl, accepted } : dl
      ))
   }, [])

   const setLineQty = useCallback((sku: string, qty: number) => {
      setDraftLines(prev => prev.flatMap(dl => {
         if (dl.sku !== sku) return [dl]
         if (qty <= 0) {
            // Ligne ajoutée par le valideur : on la fait disparaître au lieu de la marquer "supprimée"
            if (dl.isNew) return []
            return [{ ...dl, draftQty: 0, deleted: true, accepted: false }]
         }
         return [{ ...dl, draftQty: qty, accepted: true }]
      }))
   }, [])

   const setLineDiscount = useCallback((sku: string, discount: number) => {
      // 0 / NaN / champ vidé → remise retirée (undefined) : la ligne retombe sur la grille au blindage
      const clamped = Number.isFinite(discount) && discount > 0 ? Math.min(100, discount) : undefined
      setDraftLines(prev => prev.map(dl =>
         dl.sku === sku ? { ...dl, discount: clamped } : dl
      ))
   }, [])

   const deleteLine = useCallback((sku: string) => {
      setDraftLines(prev => prev.flatMap(dl => {
         if (dl.sku !== sku) return [dl]
         // Ligne ajoutée par le valideur : on la fait disparaître au lieu de la marquer "supprimée"
         if (dl.isNew) return []
         return [{ ...dl, deleted: true, accepted: false }]
      }))
   }, [])

   const undeleteLine = useCallback((sku: string) => {
      setDraftLines(prev => prev.map(dl => {
         if (dl.sku !== sku) return dl
         // Restaure la qté originale (ou draftQty si elle avait été modifiée avant suppression)
         const restoredQty = dl.draftQty > 0 ? dl.draftQty : dl.originalQty
         return { ...dl, deleted: false, accepted: true, draftQty: restoredQty || 1 }
      }))
   }, [])

   const refuseAll = useCallback(() => {
      setDraftLines(prev => prev.map(dl => ({ ...dl, accepted: false })))
   }, [])

   const addLine = useCallback((line: OrderLine) => {
      setDraftLines(prev => {
         const existing = prev.find(dl => dl.sku === line.sku)
         if (existing) {
            // SKU déjà dans le draft → cumule la qté
            return prev.map(dl =>
               dl.sku === line.sku
                  ? { ...dl, draftQty: dl.draftQty + line.qty, accepted: true, deleted: false }
                  : dl
            )
         }
         return [...prev, {
            sku: line.sku,
            originalQty: 0,
            draftQty: line.qty,
            accepted: true,
            deleted: false,
            isNew: true,
            price: line.price,
            comment: line.comment,
            discount: line.discount,
         }]
      })
   }, [])

   const reset = useCallback(() => {
      setDraftLines(buildDraftFromLines(initRef.current))
   }, [])

   const buildFinalLines = useCallback((): OrderLine[] => {
      return draftLines
         .filter(dl => !dl.deleted)
         .map(dl => {
            const line: OrderLine = { sku: dl.sku, qty: dl.draftQty }
            if (dl.price != null) line.price = dl.price
            if (dl.comment != null) line.comment = dl.comment
            if (dl.discount != null) line.discount = dl.discount
            return line
         })
   }, [draftLines])

   return {
      draftLines,
      isDirty,
      setLineAccepted,
      setLineQty,
      setLineDiscount,
      deleteLine,
      undeleteLine,
      refuseAll,
      addLine,
      reset,
      buildFinalLines,
   }
}

export default useValidationDraft
export type { DraftLine as ValidationDraftLine }
